import React from 'react';
import { Button } from '../Button';
import { useIntl } from 'react-intl';
import { StyledDetailedDeliveryCard } from './style';
import {
  DetailedDeliveryCardProps,
  DeliveryProductReference,
} from '../../types/ListingData';
import { Label } from '../Label';
import { formatDateRange } from '../../utils/dateHelpers';
import dayjs from 'dayjs';

export const DetailedDeliveryCard = ({
  delivery,
  deliveryNumber = 1,
  variant = 'add',
  onAdd,
  onEdit,
  onDelete,
  className,
  unusedProducts = [],
  availableProducts = [],
}: DetailedDeliveryCardProps) => {
  const intl = useIntl();

  const getDeliveryPeriod = () => {
    if (delivery?.delivery_start_date && delivery?.delivery_end_date) {
      return formatDateRange(
        delivery.delivery_start_date,
        delivery.delivery_end_date
      );
    }
    if (delivery?.delivery_start_date) {
      return dayjs(delivery.delivery_start_date).format('DD.MM.YYYY');
    }
    return intl.formatMessage({ id: 'listings.no_dates_set' });
  };

  const getDeliveryType = () => {
    if (delivery?.is_all_products) {
      return intl.formatMessage({ id: 'listings.form.is_all_products' });
    }
    return intl.formatMessage({ id: 'listings.selected_products' });
  };

  const getSelectedProducts = () => {
    let productsToDisplay = delivery?.selected_products || [];

    if (
      (!productsToDisplay || productsToDisplay.length === 0) &&
      delivery?.products &&
      availableProducts.length > 0
    ) {
      const result = delivery.products.map((productRef) => {
        const deliveryRef = productRef as DeliveryProductReference;
        const quantity = deliveryRef.delivery_quantity
          ? parseFloat(deliveryRef.delivery_quantity.toString())
          : 1;

        const productId = deliveryRef.listing_product_id || deliveryRef.id;

        let fullProduct = productId
          ? availableProducts.find((p) => p.id === productId)
          : null;

        if (!fullProduct && deliveryRef.category_id) {
          fullProduct = availableProducts.find(
            (p) => p.category_id === deliveryRef.category_id
          );
        }

        return {
          name:
            fullProduct?.name ||
            fullProduct?.category?.name ||
            `Product ${productRef.category_id || productRef.id}`,
          quantity,
          unit:
            fullProduct?.storage_unit?.short ||
            fullProduct?.storage_unit?.name ||
            'kg.',
        };
      });
      return result;
    }

    if (!productsToDisplay || productsToDisplay.length === 0) {
      return [];
    }

    return productsToDisplay.map((product) => {
      let quantity: number | undefined = undefined;
      if (product.category_id) {
        quantity =
          delivery?.product_quantities?.[product.category_id] ||
          (product.id ? delivery?.product_quantities?.[product.id] : undefined);
      } else if (product.id) {
        quantity = delivery?.product_quantities?.[product.id];
      }

      if (quantity === undefined) {
        const productRef = delivery?.products?.find((p) => {
          const deliveryRef = p as DeliveryProductReference;
          const refProductId = deliveryRef.listing_product_id || deliveryRef.id;
          return (
            (product.category_id &&
              deliveryRef.listing_product?.category_id ===
                product.category_id) ||
            (product.id && refProductId === product.id)
          );
        });
        if (productRef) {
          const deliveryRef = productRef as DeliveryProductReference;
          const qty = deliveryRef.delivery_quantity;
          quantity = qty ? parseFloat(qty.toString()) : 0;
        }
      }

      const unit =
        product.storage_unit?.short || product.storage_unit?.name || 'kg.';
      return {
        name: product.name || product.category?.name || '',
        quantity: quantity ?? 0,
        unit,
      };
    });
  };

  const handleAdd = () => {
    if (variant === 'add' && onAdd) {
      onAdd({});
    }
  };

  const handleEdit = () => onEdit && onEdit();
  const handleDelete = () => onDelete && onDelete();

  const selectedProducts = getSelectedProducts();

  if (variant === 'add') {
    return (
      <StyledDetailedDeliveryCard className={className}>
        <div className="delivery-info-container">
          <div className="delivery-info">
            <Label
              label={intl.formatMessage({
                id: 'listings.add_detailed_delivery_title',
              })}
              title={true}
            />
            {unusedProducts.length > 0 && (
              <div className="unused-products">
                <Label
                  label={intl.formatMessage({ id: 'listings.unused_products' })}
                />
                <div className="unused-products-list">
                  {unusedProducts.map((product, index) => (
                    <div key={index} className="unused-product-item">
                      <Label
                        label={`${product.name}: ${product.quantity} ${product.unit}.`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="delivery-actions">
            <Button
              className="action-button"
              onClick={handleAdd}
              label={intl.formatMessage({ id: 'general.add' })}
            />
          </div>
        </div>
      </StyledDetailedDeliveryCard>
    );
  } else {
    return (
      <StyledDetailedDeliveryCard className={className}>
        <div className="delivery-info-container">
          <div className="delivery-details">
            <div className="delivery-number">{deliveryNumber}</div>
            <div className="delivery-content">
              <div className="delivery-period">
                <Label label={getDeliveryPeriod()} />
              </div>
              <div className="delivery-address">
                <Label label={delivery?.delivery_address || ''} />
              </div>
              <div className="delivery-type">
                <Label label={getDeliveryType()} />
              </div>
              <div className="delivery-products">
                {selectedProducts.map((product, index) => (
                  <div key={index} className="product-item">
                    <Label
                      label={`${product.name} - (${product.quantity} ${product.unit})`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="delivery-actions">
            <Button
              className="action-button"
              onClick={handleEdit}
              label={intl.formatMessage({ id: 'general.edit' })}
            />
            <Button
              className="action-button"
              onClick={handleDelete}
              label={intl.formatMessage({ id: 'general.delete' })}
            />
          </div>
        </div>
      </StyledDetailedDeliveryCard>
    );
  }
};
